import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Modal } from 'antd';
import { FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons';
import SystemHeader from '@/widgets/layout/SystemHeader';
import AppDocContentDisplay from '@/widgets/application/AppDocContentDisplay';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import type { NavCrumb } from '../../routes';
import { RECIPIENT_CSV_LABELS, computeDeceasedMemberCodes, computeHideInstructionTextInitial, effectiveHouseholdHeadName, formatRecipientFieldValue, isDetailRowDeceased } from '@/data/recipientFields';
import { applicationDocsApi } from '@/services/applicationDocs/api';
import { isHandledAuthError } from '@/services/client';
import ConfirmDialog from '@/widgets/common/ConfirmDialog';
import NoticeDialog from '@/widgets/common/NoticeDialog';
import { FROZEN_LEFT_CLASS, isFrozenDataCol, isLastFrozenDataCol } from '@/widgets/common/detailCellStyles';
import { BackButton, DemoBadge, DemoSelect } from '@/widgets/demo/ListPrimitives';
import { demoButtonClass } from '@/widgets/demo/listHelpers';
import { recipientsApi } from '@/services/recipients/api';
import type { RecipientResponse, RecipientDetailResponse } from '@/services/recipients/types';
import { useToast, OfficeReviewStatusTag } from '@/widgets/common';
import { useOfficeReviewStatuses } from '@/hooks';
import { CASE_STATUS_CODE_MAP, caseStatusKeyByLabel, caseStatusPermissions } from '@/constants/enums';
import { usePermission } from '@/hooks/usePermission';
import { bankAccountTypeLabelFromDb } from '@/utils/bankAccountType';
import type { AppDocDataLike } from '../application/ApplicationDocDetail';

function parsePositiveInt(value: string | null): number | null {
  if (!value) return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

const BASIC_INFO_FIELD_LABELS = {
  headName: '世帯主氏名',
  addressee: RECIPIENT_CSV_LABELS[51] ?? '決定通知書宛名',
  postalCode: RECIPIENT_CSV_LABELS[52] ?? '決定通知書郵便番号（ハイフンなし）',
  address: RECIPIENT_CSV_LABELS[53] ?? '決定通知書住所',
} as const;

export interface RecipientDataLike {
  id?: number;
  identId?: string;
  importNo?: string | null;
  importNos?: string | null;
  paymentJobNo?: string | null;
  householdIdentCode?: string;
  householdNo?: string;
  benefitAmount?: number | null;
  paymentState?: string | null;
  hasApplication?: boolean | null;
  latestApplicationId?: number | null;
  attentionRequired?: boolean | null;
  officeReviewStatusId?: number | null;
  isPlaintiff?: boolean | null;
  requiresConsideration?: boolean | null;
  decisionNoticeHideInstructionText?: boolean | null;
  comment?: string;
  name?: string | null;
  address?: string | null;
  welfareStatus?: string | null;
  benefitStatus?: string;
  transferDate?: string | null;
  bankAccountHolderKana?: string | null;
  bankCode?: string | null;
  bankBranchCode?: string | null;
  bankAccountType?: number | null;
  bankAccountNumber?: string | null;
  deathInfo?: string | null;
  errorMessage?: string | null;
  householdFields?: Record<string, string> | null;
  details?: RecipientDetailResponse[];
  createdAt?: string;
  updatedAt?: string;
  registeredBy?: string | null;
  updatedBy?: string | null;
  fields?: Record<string, string>;
}

interface RecipientDetailProps {
  data: RecipientDataLike;
  appDoc: AppDocDataLike;
  baseCrumb?: NavCrumb;
  detailPageTitle?: string;
  pendingAlert?: string | null;
  clearPendingAlert?: () => void;
}

const RecipientDetail = ({ data, appDoc, baseCrumb, detailPageTitle }: RecipientDetailProps) => {
  const navigate = useAppNavigate();
  const toast = useToast();
  const { statuses: officeReviewStatuses } = useOfficeReviewStatuses();
  const { recipientId: recipientIdParam, identId: identIdParam } = useParams<{ recipientId?: string; identId?: string }>();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCashHandoverConfirm, setShowCashHandoverConfirm] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [cashHandoverPending, setCashHandoverPending] = useState(false);
  const [officeReviewSaving, setOfficeReviewSaving] = useState(false);
  const [fetchedData, setFetchedData] = useState<RecipientResponse | null>(null);
  const [considerationAlertShown, setConsiderationAlertShown] = useState<boolean>(false);
  const [considerationAlertOpen, setConsiderationAlertOpen] = useState<boolean>(false);
  // 明細テーブルの全画面表示（74列を見渡すため。Esc / 背景クリックで解除）
  const [meisaiFullscreen, setMeisaiFullscreen] = useState(false);
  useEffect(() => {
    if (!meisaiFullscreen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMeisaiFullscreen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [meisaiFullscreen]);
  const isAppDocView = detailPageTitle === '申出書詳細情報';
  const doc = appDoc || {};
  const d = fetchedData ?? data ?? {};

  // 死亡として扱う世帯員(f13)集合（死亡フラグ f17 OR 死亡情報入力の和集合）。
  // 基本情報ブロックは IIFE のためコンポーネント直下で算出し、基本情報・明細の両方から参照する。
  const deceasedMemberCodes = useMemo(
    () => computeDeceasedMemberCodes(
      (d.details || []).map((det) => ({ f13: det.fields?.f13, f17: det.fields?.f17 })),
      d.deathInfo,
    ),
    [d.details, d.deathInfo]
  );
  // 表示する世帯主氏名。明細1行目が死亡している場合は死亡していない世帯員へ繰り上げる
  // （業務ルール: 死亡者は世帯主になれない。明細を上から順に探す）。
  const effectiveHeadName = useMemo(
    () => effectiveHouseholdHeadName(
      (d.details || []).map((det) => ({ f13: det.fields?.f13, f17: det.fields?.f17 })),
      deceasedMemberCodes,
      d.name,
    ),
    [d.details, deceasedMemberCodes, d.name],
  );
  // 決定通知書宛名(f52)が死亡された世帯員か。
  const addresseeValue = (d.householdFields?.f52 ?? '').trim();
  const addresseeDeceased = addresseeValue !== '' && deceasedMemberCodes.has(addresseeValue);
  // 決定通知書教示文非表示（世帯単位）。header に明示値があればそれ、無ければ明細から初期判定。
  const hideInstructionTextDisplay = useMemo<boolean>(
    () => d.decisionNoticeHideInstructionText != null
      ? Boolean(d.decisionNoticeHideInstructionText)
      : computeHideInstructionTextInitial(
          (d.details || []).map((det) => ({ f13: det.fields?.f13, f17: det.fields?.f17, f56: det.fields?.f56 })),
          d.deathInfo,
        ),
    [d.decisionNoticeHideInstructionText, d.details, d.deathInfo]
  );
  const effectiveRecipientId = parsePositiveInt(recipientIdParam ?? null);
  const queryIdentId = (identIdParam ?? '').trim();
  const backPage = (baseCrumb && baseCrumb.page) ? baseCrumb.page : 'recipient-search';
  const backOpts = baseCrumb?.navigateOpts;

  useEffect(() => {
    let cancelled = false;

    const returnToList = (message: string) => {
      Modal.error({ content: message });
      navigate(backPage, backOpts);
    };

    const load = async () => {
      try {
        if (effectiveRecipientId != null) {
          const res = await recipientsApi.getById(effectiveRecipientId);
          if (!cancelled) setFetchedData(res.data);
          return;
        }

        if (queryIdentId) {
          const searchRes = await recipientsApi.list({ identId: queryIdentId, page: 0, size: 1 });
          const latestRecipient = searchRes.data.content?.[0];
          if (!latestRecipient?.id) {
            if (!cancelled) returnToList('対象の受給者が見つかりません。');
            return;
          }
          const detailRes = await recipientsApi.getById(latestRecipient.id);
          if (!cancelled) setFetchedData(detailRes.data);
          return;
        }

        if (!cancelled) {
          returnToList('受給者が指定されていません。');
        }
      } catch (err: unknown) {
        if (isHandledAuthError(err)) return;
        if (!cancelled) {
          const status = (err as { response?: { status?: number } } | null)?.response?.status;
          const respData = (err as { response?: { data?: { message?: string; detail?: string } } } | null)?.response?.data;
          const message = status === 404
            ? '対象の受給者が見つかりません。'
            : respData?.message || respData?.detail || '受給者詳細の取得に失敗しました。';
          returnToList(message);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [effectiveRecipientId, queryIdentId, backPage, backOpts, navigate, toast]);

  useEffect(() => {
    if (isAppDocView || considerationAlertShown) return;
    if (fetchedData && Boolean(fetchedData.requiresConsideration)) {
      // 要配慮データ取得時に一度だけ注意ダイアログを表示する。
      /* eslint-disable react-hooks/set-state-in-effect */
      setConsiderationAlertShown(true);
      setConsiderationAlertOpen(true);
      /* eslint-enable react-hooks/set-state-in-effect */
    }
  }, [fetchedData, isAppDocView, considerationAlertShown]);
  const detailNavOpts = {
    recipientId: d?.id ?? effectiveRecipientId ?? undefined,
    detailPageTitle: detailPageTitle || '受給者詳細情報',
    baseCrumb: baseCrumb || { label: '受給者／申出書 検索', page: 'recipient-search' },
  };

  const handleApprove = async () => {
    if (!doc.id || approving) return;
    setApproving(true);
    try {
      await applicationDocsApi.approve(Number(doc.id));
      toast.success('承認しました');
    } catch (error) {
      if (isHandledAuthError(error)) return;
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!doc.id || rejecting || rejectReason.trim() === '') return;
    setRejecting(true);
    try {
      await applicationDocsApi.reject(Number(doc.id), rejectReason);
      toast.success('差戻しました');
      setShowRejectConfirm(false);
      setRejectReason('');
    } catch (error) {
      if (isHandledAuthError(error)) return;
    } finally {
      setRejecting(false);
    }
  };

  // 現金手渡済操作の表示条件：給付決定通知書出力済（NOTICE_DOWNLOADED）のみ。
  const currentStatusKey = caseStatusKeyByLabel(d?.paymentState);
  const cashHandoverEligible = currentStatusKey === 'NOTICE_DOWNLOADED';

  // 編集／削除権限マトリクス（画像仕様）。世帯種別は welfareStatus に「廃止」が含まれれば保護廃止。
  const { role, hasPermission, hasAnyPermission } = usePermission();
  const canEditApp = hasPermission('APPLICATION_EDIT_NON_COMMENT');
  const canEditAppComment = hasAnyPermission('APPLICATION_EDIT_COMMENT', 'APPLICATION_EDIT_NON_COMMENT');
  const canReviewApp = hasPermission('APPLICATION_REVIEW');
  const canRecipientEdit = hasPermission('RECIPIENT_EDIT_NON_COMMENT');
  const canRecipientEditComment = hasAnyPermission('RECIPIENT_EDIT_COMMENT', 'RECIPIENT_EDIT_NON_COMMENT');
  const isWelfareEnded = (d?.welfareStatus ?? '').includes('廃止');
  const permissions = caseStatusPermissions(currentStatusKey ?? null, isWelfareEnded, role);
  const deleteDisabledTooltip = (!permissions.canDelete && currentStatusKey
    && CASE_STATUS_CODE_MAP[currentStatusKey] >= CASE_STATUS_CODE_MAP.NOTICE_DOWNLOADED)
    ? '通知書発送済の可能性があるため編集・削除不可'
    : (!permissions.canDelete && currentStatusKey
      && CASE_STATUS_CODE_MAP[currentStatusKey] >= CASE_STATUS_CODE_MAP.APP_AWAITING_SUBMIT
      && CASE_STATUS_CODE_MAP[currentStatusKey] <= CASE_STATUS_CODE_MAP.APP_REJECTED)
    ? '圧着ハガキ送付済の可能性があるため削除不可'
    : '';

  // No.25: 事務局確認用ステータスの変更。案件ステータスとは非連動で、専用APIにより即時更新する
  // （受給者編集の再計算・ステータス巻き戻しフローには一切影響しない）。
  const handleOfficeReviewChange = async (raw: string) => {
    if (!d?.id || officeReviewSaving) return;
    const nextId = raw === '' ? null : Number(raw);
    if (nextId != null && !Number.isFinite(nextId)) return;
    setOfficeReviewSaving(true);
    try {
      const res = await recipientsApi.setOfficeReviewStatus(d.id, nextId);
      setFetchedData(res.data);
      toast.success('事務局確認用ステータスを更新しました');
    } catch (error: unknown) {
      if (isHandledAuthError(error)) return;
      toast.error('事務局確認用ステータスの更新に失敗しました');
    } finally {
      setOfficeReviewSaving(false);
    }
  };

  const handleCashHandover = async () => {
    if (!d?.id || cashHandoverPending) return;
    setCashHandoverPending(true);
    try {
      const res = await recipientsApi.markCashHandover(d.id);
      setFetchedData(res.data);
      toast.success('現金手渡済として確定しました');
      setShowCashHandoverConfirm(false);
    } catch (error: unknown) {
      if (isHandledAuthError(error)) return;
    } finally {
      setCashHandoverPending(false);
    }
  };

  const appDocActions = (
    <div className="flex flex-wrap gap-2 justify-end">
      {canEditAppComment && (
        <button type="button" onClick={() => navigate('application-doc-edit', { returnPage: 'recipient-detail' })} className={demoButtonClass.secondary}>
          {canEditApp && permissions.canEdit ? '編集' : '編集（コメント）'}
        </button>
      )}
      {canEditApp && (
        <button type="button" onClick={() => setShowDeleteConfirm(true)} className="cursor-pointer rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-red-700">
          削除
        </button>
      )}
      {canReviewApp && (
        <button type="button" onClick={() => void handleApprove()} disabled={approving} className="cursor-pointer rounded-lg bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-green-700 disabled:opacity-50">
          承認
        </button>
      )}
      {canReviewApp && (
        <button type="button" onClick={() => setShowRejectConfirm(true)} disabled={rejecting} className="cursor-pointer rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-amber-700 disabled:opacity-50">
          差戻
        </button>
      )}
    </div>
  );

  const dataLoaded = fetchedData != null || data != null;
  const isDeleted = (d as Record<string, unknown>).deleted === true;

  const recipientActions = (
    <div className="flex flex-wrap gap-2 justify-end">
      {isDeleted && (
        <span className="inline-flex items-center rounded-md bg-gray-200 px-3 py-1.5 text-sm font-medium text-gray-500">削除済み</span>
      )}
      {dataLoaded && !isDeleted && canRecipientEditComment && (
        <button
          type="button"
          onClick={() => navigate('recipient-edit', detailNavOpts)}
          className={demoButtonClass.primary}
        >
          {canRecipientEdit && permissions.canEdit ? '編集' : '編集（コメント）'}
        </button>
      )}
      {dataLoaded && !isDeleted && cashHandoverEligible && hasPermission('PAYMENT_LIST_EDIT') ? (
        <button
          type="button"
          onClick={() => setShowCashHandoverConfirm(true)}
          disabled={cashHandoverPending}
          className="cursor-pointer rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-emerald-700 disabled:opacity-50"
        >
          現金手渡済
        </button>
      ) : null}
      {dataLoaded && !isDeleted && hasPermission('RECIPIENT_DELETE') && (
        <button
          type="button"
          onClick={() => {
            if (!permissions.canDelete) {
              toast.warning(deleteDisabledTooltip || '現在のステータスでは削除できません。');
              return;
            }
            setShowDeleteConfirm(true);
          }}
          title={deleteDisabledTooltip || undefined}
          className="cursor-pointer rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-red-700"
        >
          削除
        </button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader breadcrumbs={[baseCrumb || { label: '受給者／申出書 検索', page: 'recipient-search' }, { label: detailPageTitle || '受給者詳細情報' }]} />
      <div className="max-w-4xl mx-auto px-6 py-8">
        {isAppDocView ? (
          <>
            <BackButton onClick={() => navigate(backPage, backOpts)} />
            <div className="flex flex-wrap items-center justify-end gap-3 mb-6">
              {appDocActions}
            </div>

            <div className="bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl shadow-lg p-6 mb-6">
              <div className="flex justify-between items-center gap-4">
                <div>
                  <p className="text-sm text-blue-100">{detailPageTitle || '申出書詳細情報'}</p>
                  <p className="text-2xl font-bold">{doc.name}</p>
                  <p className="text-sm text-blue-200 mt-1">識別ID: {doc.identId}</p>
                  <p className="text-sm text-blue-200 mt-0.5">申請ID: {doc.id}</p>
                </div>
              </div>
            </div>

            {doc.memo ? (
              <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-6">
                <h3 className="text-sm font-bold text-gray-800 mb-3">コメント</h3>
                <p className="text-sm text-gray-800 whitespace-pre-wrap min-h-[3rem]">{doc.memo}</p>
              </div>
            ) : null}

            <AppDocContentDisplay data={doc} hideMemo />

            <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
              {appDocActions}
            </div>
          </>
        ) : (
          <>
            <BackButton onClick={() => navigate(backPage, backOpts)} />
            {d.errorMessage && String(d.errorMessage).trim() !== '' ? (
              <div className="mb-6 rounded-xl border border-red-300 bg-red-50 p-4 shadow-sm" role="alert">
                <div className="flex items-start gap-3">
                  <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M18 10A8 8 0 11 2 10a8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div className="min-w-0">
                    <h3 className="text-sm font-bold text-red-800">エラー情報</h3>
                    <p className="mt-1 text-sm text-red-700 whitespace-pre-wrap break-words">{d.errorMessage}</p>
                  </div>
                </div>
              </div>
            ) : null}
            <div className="flex flex-wrap items-center justify-end gap-3 mb-6">
              {recipientActions}
            </div>

            <div className="mb-4 flex flex-wrap items-center gap-3">
              <p className="text-sm text-gray-600">
                識別ID: <span className="font-mono font-medium text-gray-900">{d.identId}</span>
              </p>
              {d.paymentState && d.paymentState !== '—' ? (
                <DemoBadge status={d.paymentState} />
              ) : null}
              {d.isPlaintiff ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800">
                  原告
                </span>
              ) : null}
              {d.requiresConsideration ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                  要配慮
                </span>
              ) : null}
            </div>

            {/* 事務局確認用ステータス（No.25。案件ステータスとは非連動の手動分類） */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-6">
              <h3 className="text-sm font-bold text-gray-800 mb-3">事務局確認用ステータス</h3>
              <div className="flex flex-wrap items-center gap-3">
                <OfficeReviewStatusTag statusId={d.officeReviewStatusId} />
                {canRecipientEditComment ? (
                  <DemoSelect
                    value={d.officeReviewStatusId != null ? String(d.officeReviewStatusId) : ''}
                    disabled={officeReviewSaving}
                    onChange={(e) => void handleOfficeReviewChange(e.target.value)}
                    className="max-w-xs"
                  >
                    <option value="">未設定</option>
                    {officeReviewStatuses.map((s) => (
                      <option key={s.id} value={String(s.id)}>
                        {s.displayName}
                      </option>
                    ))}
                  </DemoSelect>
                ) : null}
              </div>
              <p className="mt-2 text-xs text-gray-400">
                案件の進行状況（ステータス）とは別に、事務局内の確認・整理用に手動で設定する分類です。
              </p>
            </div>

            {/* コメント */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-6">
              <h3 className="text-sm font-bold text-gray-800 mb-3">コメント</h3>
              <p className="text-sm text-gray-800 whitespace-pre-wrap min-h-[3rem]">
                {d.comment != null && String(d.comment).trim() !== '' ? d.comment : '—'}
              </p>
            </div>

            {/* 死亡情報入力 */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-6">
              <h3 className="text-sm font-bold text-gray-800 mb-3">死亡情報入力</h3>
              <p className="text-sm text-gray-800 whitespace-pre-wrap min-h-[2rem]">
                {d.deathInfo != null && String(d.deathInfo).trim() !== '' ? d.deathInfo : '—'}
              </p>
              <p className="mt-1 text-xs text-gray-400">複数の場合はカンマ区切りで入力</p>
            </div>

            {/* 基本情報 */}
            {(() => {
              const hf = d.householdFields || {};
              const field = (key: string) => hf[key] != null && hf[key] !== '' ? hf[key] : '—';
              const transferDateDisplay =
                d.paymentState === '審査中'
                  ? 'ー'
                  : d.transferDate != null && String(d.transferDate).trim() !== ''
                    ? String(d.transferDate).trim()
                    : 'ー';
              const benefitStatusKey = caseStatusKeyByLabel(d?.paymentState);
              const benefitCalculated = benefitStatusKey != null
                && CASE_STATUS_CODE_MAP[benefitStatusKey] >= CASE_STATUS_CODE_MAP.CALC_DONE;
              const benefitAmountDisplay = benefitCalculated && d?.benefitAmount != null && !Number.isNaN(Number(d.benefitAmount))
                ? `${Number(d.benefitAmount).toLocaleString()}円`
                : 'ー';
              return (
                <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 mb-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-5 pb-3 border-b border-gray-200">基本情報</h2>
                  <dl className="space-y-5">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{BASIC_INFO_FIELD_LABELS.headName}</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {effectiveHeadName !== '' ? effectiveHeadName : '—'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{BASIC_INFO_FIELD_LABELS.addressee}</dt>
                      <dd className="mt-1 text-sm text-gray-900">{field('f52')}</dd>
                      {addresseeDeceased ? (
                        <p className="mt-1 text-xs text-amber-700">
                          宛名「{addresseeValue}」は死亡された方として登録されています。
                        </p>
                      ) : null}
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{BASIC_INFO_FIELD_LABELS.postalCode}</dt>
                      <dd className="mt-1 text-sm text-gray-900 font-mono">{field('f53')}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">{BASIC_INFO_FIELD_LABELS.address}</dt>
                      <dd className="mt-1 text-sm text-gray-900 break-words">{field('f54')}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">決定通知書教示文非表示</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {hideInstructionTextDisplay ? (
                          <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                            非表示
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                            表示
                          </span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">給付金額</dt>
                      <dd className="mt-1 text-sm text-gray-900">{benefitAmountDisplay}</dd>
                      <p className="mt-1 text-xs text-gray-500">※この給付金額は保護給の追加給付のみであり、別途原告に支給される「特別給付金」は含まれておりません。</p>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">振込予定日</dt>
                      <dd className="mt-1 text-sm text-gray-900">{transferDateDisplay}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">原告フラグ</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {d.isPlaintiff ? (
                          <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800">
                            原告
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                            非原告
                          </span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">要配慮</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {d.requiresConsideration ? (
                          <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800">
                            要配慮
                          </span>
                        ) : (
                          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                            ー
                          </span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500 mb-2">口座情報</dt>
                      <dd>
                        <div className="rounded-lg border border-gray-200 bg-gray-50/80 px-4 py-3 text-sm text-gray-900">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                            <div>
                              <span className="text-gray-500">銀行番号</span>
                              <span className="ml-2 font-mono">{d.bankCode || '—'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">支店番号</span>
                              <span className="ml-2 font-mono">{d.bankBranchCode || '—'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">預金種目</span>
                              <span className="ml-2">{bankAccountTypeLabelFromDb(d.bankAccountType) || '—'}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">口座番号</span>
                              <span className="ml-2 font-mono">{d.bankAccountNumber || '—'}</span>
                            </div>
                            <div className="sm:col-span-2">
                              <span className="text-gray-500">口座名義</span>
                              <span className="ml-2">{d.bankAccountHolderKana || '—'}</span>
                            </div>
                          </div>
                        </div>
                      </dd>
                    </div>
                  </dl>
                </div>
              );
            })()}

            {/* 明細 */}
            {(() => {
              const detailRows = (d.details || []).map((det: RecipientDetailResponse, idx: number) => ({
                lineNo: String(idx + 1),
                cells: RECIPIENT_CSV_LABELS.map((_: string, ci: number) => {
                  const key = 'f' + (ci + 1);
                  return formatRecipientFieldValue(key, det.fields?.[key]);
                }),
              }));
              if (detailRows.length === 0) {
                return (
                  <div className="bg-white rounded-xl shadow-lg border border-gray-200 mb-6 overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                      <h3 className="text-lg font-bold text-gray-800">明細</h3>
                    </div>
                    <div className="px-4 py-8 text-center text-sm text-gray-500">
                      該当するデータがありません
                    </div>
                  </div>
                );
              }
              return (
                <>
                {meisaiFullscreen ? (
                  <div className="fixed inset-0 z-[55] bg-black/40" aria-hidden onClick={() => setMeisaiFullscreen(false)} />
                ) : null}
                <div className={
                  meisaiFullscreen
                    ? 'fixed inset-2 sm:inset-4 z-[60] flex flex-col bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden'
                    : 'bg-white rounded-xl shadow-lg border border-gray-200 mb-6 overflow-hidden'
                }>
                  <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 rounded-t-xl flex flex-wrap items-baseline justify-between gap-2">
                    <h3 className="text-lg font-bold text-gray-800">明細</h3>
                    <div className="flex flex-wrap items-center gap-3">
                      <p className="text-sm text-gray-500">
                        <span className="font-mono font-medium text-gray-700">{RECIPIENT_CSV_LABELS.length}</span>
                        項目 × 全
                        <span className="font-mono font-medium text-gray-700">{detailRows.length}</span>
                        件（横＝項目・縦＝件数。ヘッダー行・左側項目は固定）
                      </p>
                      <div role="button" tabIndex={0}
                        onClick={() => setMeisaiFullscreen(v => !v)}
                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMeisaiFullscreen(v => !v); } }}
                        title={meisaiFullscreen ? '全画面を解除（Esc）' : '全画面で表示'}
                        className="inline-flex items-center shrink-0 whitespace-nowrap gap-1.5 h-8 px-3 text-xs text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 transition cursor-pointer select-none">
                        {meisaiFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                        <span>{meisaiFullscreen ? '全画面解除' : '全画面'}</span>
                      </div>
                    </div>
                  </div>
                  <div
                    className={`meisai-scroll-x ${meisaiFullscreen ? 'flex-1 min-h-0' : 'max-h-[min(70vh,36rem)]'} overflow-auto overscroll-contain`}
                    onScroll={(e) => { const el = e.currentTarget; const v = el.scrollLeft > 0 ? '1' : '0'; if (el.getAttribute('data-scrolledx') !== v) el.setAttribute('data-scrolledx', v); }}
                  >
                    <table className="min-w-max text-xs border-separate border-spacing-0">
                      <colgroup>
                        <col className="w-12 min-w-[3rem]" />
                        {RECIPIENT_CSV_LABELS.map((_: string, colIdx: number) => (
                          <col key={`cg-${colIdx}`} className="min-w-[6.5rem] w-[6.5rem]" />
                        ))}
                      </colgroup>
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th scope="col"
                            className="sticky top-0 left-0 z-30 box-border text-left font-semibold text-gray-800 px-2 py-2 bg-gray-200 border-r border-gray-300 w-12 min-w-[3rem]"
                          >No.</th>
                          {RECIPIENT_CSV_LABELS.map((label: string, colIdx: number) => {
                            const frozen = isFrozenDataCol(colIdx);
                            const lastFrozen = isLastFrozenDataCol(colIdx);
                            return (
                              <th key={`h-${colIdx}`} scope="col" title={label}
                                className={`sticky top-0 ${frozen ? `${FROZEN_LEFT_CLASS[colIdx]} z-30` : 'z-20'} box-border text-left font-semibold text-gray-700 px-2 py-2 bg-gray-100 border-b border-gray-200 whitespace-nowrap min-w-[6.5rem] w-[6.5rem] truncate ${lastFrozen ? 'border-r border-transparent meisai-freeze-edge' : 'shadow-[0_1px_0_0_rgb(229,231,235)]'}`}
                              >{label}</th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {detailRows.map((row: { lineNo: string; cells: string[] }, rowIdx: number) => {
                          const detSrc = d.details?.[rowIdx];
                          const rowDeceased = isDetailRowDeceased(
                            { f13: detSrc?.fields?.f13, f17: detSrc?.fields?.f17 },
                            deceasedMemberCodes,
                          );
                          // bg-* は1つに絞る（bg-white と bg-rose-50 併記は CSS 出力順依存になるため）。
                          const stripe = rowDeceased
                            ? 'bg-rose-50'
                            : (rowIdx % 2 === 0 ? 'bg-white' : 'bg-slate-100');
                          return (
                            <tr key={`${row.lineNo}-${rowIdx}`} className={stripe}>
                              <td className={`sticky left-0 z-10 box-border px-2 py-1.5 border-b border-r border-gray-200 text-gray-800 font-mono text-right w-12 min-w-[3rem] ${stripe}`}>
                                {rowDeceased ? (
                                  <span className="mr-0.5 text-red-600" title="この世帯員は死亡として登録されています">死</span>
                                ) : null}
                                {row.lineNo}
                              </td>
                              {row.cells.map((val: string, colIdx: number) => {
                                const frozen = isFrozenDataCol(colIdx);
                                const lastFrozen = isLastFrozenDataCol(colIdx);
                                return (
                                  <td key={`r-${rowIdx}-c-${colIdx}`}
                                    className={`box-border px-2 py-1.5 border-b border-gray-100 text-gray-900 whitespace-nowrap min-w-[6.5rem] w-[6.5rem] truncate ${stripe} ${frozen ? `sticky ${FROZEN_LEFT_CLASS[colIdx]} z-10` : ''} ${lastFrozen ? 'border-r border-transparent meisai-freeze-edge' : ''}`}
                                    title={val != null && String(val) !== '' ? String(val) : '—'}
                                  >
                                    {val != null && String(val) !== '' ? val : '—'}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                </>
              );
            })()}

            <div className="flex flex-wrap items-center justify-end gap-3 mt-2 pt-6 border-t border-gray-200">
              {recipientActions}
            </div>
          </>
        )}

        <NoticeDialog
          open={considerationAlertOpen}
          title="要配慮の受給者です"
          message={'この受給者は「要配慮」に指定されています。\n対応にあたっては内容に十分ご注意ください。'}
          confirmLabel="確認しました"
          variant="warning"
          onClose={() => setConsiderationAlertOpen(false)}
        />

        <ConfirmDialog
          open={showDeleteConfirm}
          message={isAppDocView ? 'この申出書情報を削除してもよろしいですか？' : 'この受給者情報を削除してもよろしいですか？'}
          variant="danger"
          onConfirm={async () => {
            try {
              if (d?.id) {
                await recipientsApi.delete(d.id);
              }
              setShowDeleteConfirm(false);
              toast.success('削除しました。');
              navigate(backPage, backOpts);
            } catch (e: unknown) {
              setShowDeleteConfirm(false);
              if (isHandledAuthError(e)) return;
            }
          }}
          onCancel={() => setShowDeleteConfirm(false)}
        />

        <ConfirmDialog
          open={showCashHandoverConfirm}
          title="現金手渡済確定"
          message={'この受給者を現金手渡済として最終確定します。\n以降は全銀協出力の対象外となります。\nよろしいですか？'}
          confirmLabel="確定する"
          cancelLabel="キャンセル"
          variant="danger"
          loading={cashHandoverPending}
          onConfirm={() => void handleCashHandover()}
          onCancel={() => setShowCashHandoverConfirm(false)}
        />

        {showRejectConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4">
              <h3 className="text-lg font-bold text-gray-900 mb-4">差戻確認</h3>
              <p className="text-sm text-gray-600 mb-3">差戻理由を入力してください。</p>
              <textarea
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500 resize-none"
                rows={4}
                placeholder="差戻理由を入力してください（必須）"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
              />
              <div className="flex justify-end gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => { setShowRejectConfirm(false); setRejectReason(''); }}
                  className="cursor-pointer rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  キャンセル
                </button>
                <button
                  type="button"
                  onClick={() => void handleReject()}
                  disabled={rejecting || rejectReason.trim() === ''}
                  className="cursor-pointer rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-amber-700 disabled:opacity-50"
                >
                  {rejecting ? '処理中...' : 'OK'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecipientDetail;
